import React, { useState, useEffect } from 'react';
import { adminAPI, scheduleProactiveRefresh } from '../services/api';
import { useAuth } from '../context/AuthContext';

const ImpersonationBanner = () => {
  const { user, refreshProfile } = useAuth();
  const [stopping, setStopping] = useState(false);

  const impersonation = user?.impersonating ? {
    target: {
      id: user.impersonating.id,
      email: user.impersonating.email,
      firstname: user.impersonating.firstname,
      lastname: user.impersonating.lastname,
    },
    expires_at: user.impersonating.expires_at,
  } : null;

  const handleStop = async () => {
    setStopping(true);
    try {
      await adminAPI.stopImpersonation();
      scheduleProactiveRefresh();
      await refreshProfile();
      window.location.href = '/admin';
    } catch (err) {
      console.error('Failed to stop impersonation:', err);
      setStopping(false);
    }
  };

  if (!impersonation) return null;

  const expiresAt = impersonation.expires_at;
  const timeRemaining = expiresAt ? Math.max(0, Math.floor((expiresAt - Date.now()) / 1000)) : null;
  const minutesLeft = timeRemaining !== null ? Math.floor(timeRemaining / 60) : null;
  const secondsLeft = timeRemaining !== null ? timeRemaining % 60 : null;
  const isExpiringSoon = timeRemaining !== null && timeRemaining < 300;

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: isExpiringSoon ? 'linear-gradient(90deg, #ea580c, #c2410c)' : 'linear-gradient(90deg, #dc2626, #b91c1c)',
      color: 'white', padding: '8px 16px', display: 'flex',
      alignItems: 'center', justifyContent: 'center', gap: '16px',
      fontSize: '14px', fontWeight: 500, boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4c-.77-1.33-2.69-1.33-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
      </svg>
      <span>
        <strong>Impersonating:</strong>{' '}
        {impersonation.target?.firstname} {impersonation.target?.lastname}{' '}
        ({impersonation.target?.email})
      </span>
      {minutesLeft !== null && (
        <span style={{ opacity: 0.9, fontSize: '12px' }}>
          Session expires in {minutesLeft}:{secondsLeft.toString().padStart(2, '0')}
        </span>
      )}
      <button
        onClick={handleStop}
        disabled={stopping}
        style={{
          background: 'white', color: '#dc2626', border: 'none',
          padding: '5px 16px', borderRadius: '6px', fontWeight: 600,
          fontSize: '13px', cursor: stopping ? 'not-allowed' : 'pointer',
          opacity: stopping ? 0.7 : 1, flexShrink: 0,
        }}
      >
        {stopping ? 'Stopping...' : 'Stop Impersonating'}
      </button>
    </div>
  );
};

// Helper to start impersonation — tokens are set as cookies by the backend
export const startImpersonation = async (userId, companyId = null) => {
  const response = await adminAPI.startImpersonation(userId, companyId);

  if (response.status !== 'success') {
    throw new Error('Failed to start impersonation');
  }

  scheduleProactiveRefresh();
};

export default ImpersonationBanner;
